import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "../../legacy/services/apiClient";
import HeroSearchBar from "../search/HeroSearchBar.jsx";
import { resolveMediaUrl } from "../../utils/resolveMediaUrl";

const SLIDE_INTERVAL = 6000;
const HERO_TYPES = ["full", "split3", "split2"];
const SPLIT_POSITIONS = {
  split3: ["left", "middle", "right"],
  split2: ["left", "right"],
};
const SPLIT_LABELS = {
  split3: {
    left: "Luxury Villas",
    middle: "Packages",
    right: "Cab Services",
  },
  split2: {
    left: "Left Panel",
    right: "Right Panel",
  },
};

const toStr = (value) => (value ?? "").toString().trim();
const toNum = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const isExternal = (href) => /^https?:\/\//i.test(String(href || ""));
const isHeroType = (value) => HERO_TYPES.includes(toStr(value).toLowerCase());
const isYoutubeUrl = (value) => /youtube\.com|youtu\.be/i.test(String(value || ""));

const isVideoFileUrl = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return false;
  const clean = raw.split("?")[0].split("#")[0];
  return [".mp4", ".webm", ".ogg", ".mov"].some((ext) => clean.endsWith(ext));
};

const toYoutubeEmbedUrl = (value) => {
  try {
    const url = new URL(String(value || ""));
    if (url.hostname.includes("youtu.be")) {
      const id = url.pathname.replace("/", "");
      return id ? `https://www.youtube.com/embed/${id}` : value;
    }
    const id = url.searchParams.get("v");
    return id ? `https://www.youtube.com/embed/${id}` : value;
  } catch (_) {
    return value;
  }
};

const HeroImage = ({ src, alt, className, loading = "lazy" }) => {
  const resolved = resolveMediaUrl(src, {
    width: loading === "eager" ? 1600 : 960,
    height: 520,
    fit: "cover",
    quality: loading === "eager" ? 76 : 70,
  });
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [resolved]);

  if (!resolved || failed) return null;
  return (
    <img
      src={resolved}
      alt={alt || ""}
      className={className}
      loading={loading}
      fetchPriority={loading === "eager" ? "high" : "auto"}
      onError={() => setFailed(true)}
    />
  );
};

const normalizeSplitItems = (items, bannerType) => {
  const source = Array.isArray(items) ? items : [];
  const positions = SPLIT_POSITIONS[bannerType] || [];
  const labels = SPLIT_LABELS[bannerType] || {};
  const byPosition = new Map();

  source.forEach((item, index) => {
    const pos = toStr(item?.position || positions[index] || `item-${index + 1}`).toLowerCase();
    if (!pos) return;
    byPosition.set(pos, item);
  });

  const mapItem = (item = {}, pos = "", index = 0) => {
    const label = toStr(item.label || labels[pos] || item.title || pos || `Item ${index + 1}`);
    return {
      id: item.id || item._id || `${bannerType}-${pos || index + 1}`,
      position: pos || toStr(item.position || `item-${index + 1}`).toLowerCase(),
      label,
      title: toStr(item.title),
      description: toStr(item.description || item.subtitle || item.desc),
      imageUrl: resolveMediaUrl(item.image_url || item.imageUrl || item.image),
      videoUrl: resolveMediaUrl(item.video_url || item.videoUrl),
      overlayText: toStr(item.overlay_text || item.overlayText || item.title || label),
      isActive: item.is_active !== false && item.isActive !== false && item.active !== false,
    };
  };

  if (!positions.length) {
    return source.map((item, index) => mapItem(item, "", index));
  }

  return positions.map((pos, index) => mapItem(byPosition.get(pos) || {}, pos, index));
};

const normalizeBanner = (item) => {
  const typeRaw = toStr(item?.banner_type || item?.bannerType || item?.type).toLowerCase();
  const bannerType = typeRaw === "primary" ? "full" : typeRaw;
  if (!isHeroType(bannerType)) return null;

  return {
    id: item?.id || item?._id || bannerType,
    banner_type: bannerType,
    banner_order: toNum(item?.banner_order ?? item?.bannerOrder ?? item?.sort_order, 0),
    image_url: resolveMediaUrl(item?.image_url || item?.imageUrl || item?.image),
    video_url: resolveMediaUrl(item?.video_url || item?.videoUrl),
    title: toStr(item?.title || item?.heading),
    subtitle: toStr(item?.subtitle || item?.subheading),
    cta_text: toStr(item?.cta_text || item?.ctaText || item?.primary_cta_text),
    cta_link: toStr(item?.cta_link || item?.ctaLink || item?.primary_cta_link),
    split_items: bannerType === "full" ? [] : normalizeSplitItems(item?.split_items || item?.splitItems, bannerType),
    is_active: item?.is_active !== false && item?.isActive !== false && item?.active !== false,
  };
};

const extractBannerList = (payload) => {
  const data = payload?.data ?? payload;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.value)) return data.value;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
};

const hasMedia = (banner) => {
  if (!banner) return false;
  if (banner.banner_type === "full") {
    return Boolean(toStr(banner.image_url || banner.video_url));
  }
  const items = Array.isArray(banner.split_items) ? banner.split_items : [];
  return items.some((item) => Boolean(toStr(item.imageUrl || item.videoUrl)));
};

export default function HeroBanner({
  preferRemote = true,
  showAll = false,
  showAllLayout = "stacked",
  fallbackContent = {},
  fixedBanners = [],
}) {
  const [banners, setBanners] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const lockedBanners = useMemo(() => {
    const normalized = (Array.isArray(fixedBanners) ? fixedBanners : [])
      .map(normalizeBanner)
      .filter(Boolean)
      .filter((banner) => banner.is_active !== false)
      .filter((banner) => hasMedia(banner))
      .sort((a, b) => Number(a.banner_order || 0) - Number(b.banner_order || 0));
    const byType = new Map();
    normalized.forEach((banner) => {
      const type = toStr(banner?.banner_type).toLowerCase();
      if (!isHeroType(type)) return;
      if (!byType.has(type)) byType.set(type, banner);
    });
    return HERO_TYPES.map((type) => byType.get(type)).filter(Boolean);
  }, [fixedBanners]);
  const hasLockedBanners = lockedBanners.length > 0;

  useEffect(() => {
    let active = true;
    if (preferRemote === false && hasLockedBanners) {
      setBanners([]);
      setActiveIndex(0);
      return () => {
        active = false;
      };
    }

    (async () => {
      try {
        const res = await apiGet(`/api/public/hero-banners?_ts=${Date.now()}`);
        const list = extractBannerList(res);
        const normalized = list
          .map(normalizeBanner)
          .filter(Boolean)
          .filter((banner) => banner.is_active !== false)
          .filter((banner) => hasMedia(banner))
          .sort((a, b) => Number(a.banner_order || 0) - Number(b.banner_order || 0));
        const byType = new Map();
        normalized.forEach((banner) => {
          const type = toStr(banner?.banner_type).toLowerCase();
          if (!isHeroType(type)) return;
          if (!byType.has(type)) byType.set(type, banner);
        });
        const canonical = HERO_TYPES.map((type) => byType.get(type)).filter(Boolean);
        if (!active) return;
        setBanners(canonical);
        setActiveIndex(0);
      } catch (_) {
        if (!active) return;
        setBanners([]);
        setActiveIndex(0);
      }
    })();

    return () => {
      active = false;
    };
  }, [hasLockedBanners, preferRemote]);

  const effectiveBanners = useMemo(() => {
    if (preferRemote) {
      return banners.length ? banners : lockedBanners;
    }
    return hasLockedBanners ? lockedBanners : banners;
  }, [banners, hasLockedBanners, lockedBanners, preferRemote]);

  const slides = useMemo(() => {
    const ordered = [...effectiveBanners].sort(
      (a, b) => Number(a.banner_order || 0) - Number(b.banner_order || 0)
    );
    if (!showAll) return ordered;
    const byType = new Map(ordered.map((banner) => [toStr(banner.banner_type).toLowerCase(), banner]));
    return HERO_TYPES.map((type) => byType.get(type)).filter(Boolean);
  }, [effectiveBanners, showAll]);

  const totalSlides = slides.length;
  const canSlide = !showAll && totalSlides > 1;
  const isCarouselLayout = showAll && showAllLayout === "carousel";
  const isStackedLayout = showAll && showAllLayout !== "carousel";

  useEffect(() => {
    if (!canSlide || isPaused) return undefined;
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % totalSlides);
    }, SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, [canSlide, isPaused, totalSlides]);

  useEffect(() => {
    if (!totalSlides) return;
    if (activeIndex >= totalSlides) setActiveIndex(0);
  }, [activeIndex, totalSlides]);

  const goPrev = () => {
    if (!totalSlides) return;
    setActiveIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goNext = () => {
    if (!totalSlides) return;
    setActiveIndex((prev) => (prev + 1) % totalSlides);
  };

  const renderFullBanner = (banner, priority = false) => {
    const ctaLabel = banner.cta_text || "Explore stays";
    const ctaLink = banner.cta_link || "/listings";
    const hideText =
      banner.hide_text ||
      banner.hideText ||
      banner?.meta?.hide_text ||
      banner?.meta?.hideText;
    const videoSrc = banner.video_url;
    const imageSrc = banner.image_url;

    return (
      <div className="relative h-full w-full">
        <div className="absolute inset-0 z-0 bg-[linear-gradient(140deg,#2d2722_0%,#1f1b17_100%)]">
          {videoSrc ? (
            isYoutubeUrl(videoSrc) ? (
              <iframe
                title={banner.title || "Hero video"}
                className="h-full w-full"
                src={toYoutubeEmbedUrl(videoSrc)}
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            ) : (
              <video className="h-full w-full object-cover" autoPlay={priority} muted loop playsInline preload={priority ? "auto" : "none"}>
                <source src={videoSrc} />
              </video>
            )
          ) : (
            <HeroImage
              src={imageSrc}
              alt={banner.title || "Hero banner"}
              className="h-full w-full object-cover"
              loading={priority ? "eager" : "lazy"}
            />
          )}
        </div>
        <div className="absolute inset-0 z-10" style={{ background: "linear-gradient(120deg, rgba(7,20,36,0.72) 0%, rgba(7,20,36,0.45) 48%, rgba(7,20,36,0.22) 100%)" }} />
        {!hideText ? (
          <div className="absolute inset-0 z-20">
            <div className="mx-auto flex h-full max-w-[1280px] items-center px-6 sm:px-10 lg:px-[72px]">
              <div className="max-w-[560px] space-y-5 text-left text-white">
                <h1 className="text-5xl font-homevilas leading-[0.92] md:text-[5rem]">
                  {banner.title || ""}
                </h1>
                <p className="max-w-[48ch] text-sm leading-7 text-white/84 md:text-base">
                  {banner.subtitle || ""}
                </p>
                {isExternal(ctaLink) ? (
                  <a
                    href={ctaLink}
                    className="inline-flex items-center justify-center rounded-full bg-[var(--cta-dark)] px-6 py-3 text-xs font-medium uppercase tracking-[0.24em] text-white shadow-soft-md transition hover:bg-[var(--cta-dark-hover)]"
                  >
                    {ctaLabel}
                  </a>
                ) : (
                  <Link
                    to={ctaLink}
                    className="inline-flex items-center justify-center rounded-full bg-[var(--cta-dark)] px-6 py-3 text-xs font-medium uppercase tracking-[0.24em] text-white shadow-soft-md transition hover:bg-[var(--cta-dark-hover)]"
                  >
                    {ctaLabel}
                  </Link>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderSplit3 = (banner, priority = false) => {
    const items = Array.isArray(banner.split_items) ? banner.split_items : [];
    return (
      <div className="grid h-full grid-cols-3">
        {items.map((item, index) => {
          const isActive = item.isActive !== false;
          const label = item.label || SPLIT_LABELS.split3[item.position] || item.title || "";
          const imageSrc = item.imageUrl || "";
          const key = item.id || item.position || `${banner.id}-split3-${index}`;
          return (
            <div key={key} className="relative h-full">
              <div className="absolute inset-0 z-0 bg-[linear-gradient(140deg,#2d2722_0%,#1f1b17_100%)]">
                <HeroImage
                  src={imageSrc}
                  alt={item.title || label}
                  className={`h-full w-full object-cover ${isActive ? "" : "opacity-40 grayscale"}`}
                  loading={priority ? "eager" : "lazy"}
                />
              </div>
              <div className="absolute inset-0 z-10" style={{ background: "linear-gradient(180deg, rgba(7,20,36,0.1) 0%, rgba(7,20,36,0.52) 100%)" }} />
              <div className="absolute inset-0 z-20 flex items-end p-6">
                <div className="max-w-[280px] space-y-2 text-white">
                  <h3 className="text-[1.7rem] font-homevilas leading-[0.95]">{item.title || label}</h3>
                  {item.description ? <p className="text-xs text-white/80">{item.description}</p> : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSplit2 = (banner, priority = false) => {
    const items = Array.isArray(banner.split_items) ? banner.split_items : [];
    return (
      <div className="grid h-full grid-cols-2">
        {items.map((item, index) => {
          const isActive = item.isActive !== false;
          const label = item.label || SPLIT_LABELS.split2[item.position] || "";
          const overlayText = item.overlayText || item.title || label;
          const rawVideo = item.videoUrl || "";
          const rawImage = item.imageUrl || "";
          const resolvedVideo = rawVideo || (isVideoFileUrl(rawImage) ? rawImage : "");
          const resolvedImage = resolvedVideo ? "" : rawImage;
          const key = item.id || item.position || `${banner.id}-split2-${index}`;

          return (
            <div key={key} className="relative h-full">
              {resolvedVideo ? (
                isYoutubeUrl(resolvedVideo) ? (
                  <iframe
                    title={overlayText || "Panel video"}
                    className={`h-full w-full ${isActive ? "" : "opacity-40 grayscale"}`}
                    src={toYoutubeEmbedUrl(resolvedVideo)}
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                  />
                ) : (
                  <video
                    className={`h-full w-full object-cover ${isActive ? "" : "opacity-40 grayscale"}`}
                    autoPlay={priority}
                    muted
                    loop
                    playsInline
                    preload={priority ? "auto" : "none"}
                  >
                    <source src={resolvedVideo} />
                  </video>
                )
              ) : (
                <div className="absolute inset-0 z-0 bg-[linear-gradient(135deg,#24201c_0%,#1a1714_100%)]">
                  <HeroImage
                    src={resolvedImage}
                    alt={overlayText}
                    className={`h-full w-full object-cover ${isActive ? "" : "opacity-40 grayscale"}`}
                    loading={priority ? "eager" : "lazy"}
                  />
                </div>
              )}
              <div className="absolute inset-0 z-10" style={{ background: "linear-gradient(180deg, rgba(7,20,36,0.16) 0%, rgba(7,20,36,0.56) 100%)" }} />
              <div className="absolute inset-0 z-20 flex items-end p-6">
                <div className="max-w-[320px] text-white">
                  <h3 className="text-[1.7rem] font-homevilas leading-[0.95]">{overlayText}</h3>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSlide = (banner, index, className = "h-full min-w-0 flex-1") => (
    <div key={banner.id || `${banner.banner_type}-${index}`} className={className}>
      {banner.banner_type === "split3"
        ? renderSplit3(banner, index === activeIndex)
        : banner.banner_type === "split2"
        ? renderSplit2(banner, index === activeIndex)
        : renderFullBanner(banner, index === activeIndex)}
    </div>
  );

  const fallbackLocationPills = Array.isArray(fallbackContent.locationPills)
    ? fallbackContent.locationPills.filter(Boolean).slice(0, 4)
    : [];

  if (!slides.length) {
    const hasFallbackContent = Boolean(
      fallbackContent.heroEyebrow ||
        fallbackContent.heroHeadline ||
        fallbackContent.heroSubhead ||
        fallbackContent.heroPrimaryLink ||
        fallbackContent.heroSecondaryLink ||
        fallbackLocationPills.length
    );
    if (!hasFallbackContent) return null;

    return (
      <section className="relative bg-transparent">
        <div className="hv-hero-shell relative overflow-hidden pb-[112px] md:pb-[120px]">
          <div className="relative h-[360px] sm:h-[400px] lg:h-[432px] bg-[linear-gradient(135deg,#10233d_0%,#071424_48%,#0f5c5a_100%)]">
            <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(7,20,36,0.3) 0%, rgba(7,20,36,0.45) 55%, rgba(7,20,36,0.62) 100%)" }} />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(201,161,91,0.18),transparent_26%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.08),transparent_32%)]" />
            <div className="relative mx-auto flex h-full max-w-[1280px] items-center px-6 sm:px-10 lg:px-[72px]">
              <div className="max-w-[620px] space-y-5 text-white">
                {fallbackContent.heroEyebrow ? (
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] font-medium uppercase tracking-[0.38em] text-white/72">
                      {fallbackContent.heroEyebrow}
                    </span>
                    <span className="h-px w-16 bg-[rgba(201,161,91,0.8)]" />
                  </div>
                ) : null}
                <h1 className="text-5xl font-homevilas leading-[0.9] md:text-[5.2rem]">
                  {fallbackContent.heroHeadline || "Luxury stays shaped for slower, richer travel."}
                </h1>
                <p className="max-w-[50ch] text-sm leading-7 text-white/82 md:text-base">
                  {fallbackContent.heroSubhead || "Curated villas, homestays, and journeys designed for elevated escapes across India."}
                </p>
                <div className="flex flex-wrap gap-3">
                  {fallbackContent.heroPrimaryLink ? (
                    <Link
                      to={fallbackContent.heroPrimaryLink}
                      className="inline-flex items-center justify-center rounded-full bg-[var(--cta-dark)] px-6 py-3 text-xs font-medium uppercase tracking-[0.24em] text-white shadow-soft-md transition hover:bg-[var(--cta-dark-hover)]"
                    >
                      {fallbackContent.heroPrimaryLabel || "Explore stays"}
                    </Link>
                  ) : null}
                  {fallbackContent.heroSecondaryLink ? (
                    <Link
                      to={fallbackContent.heroSecondaryLink}
                      className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-6 py-3 text-xs font-medium uppercase tracking-[0.24em] text-white transition hover:bg-white/16"
                    >
                      {fallbackContent.heroSecondaryLabel || "Discover packages"}
                    </Link>
                  ) : null}
                </div>
                {fallbackLocationPills.length ? (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {fallbackLocationPills.map((pill) => (
                      <span
                        key={pill}
                        className="rounded-full border border-white/16 bg-white/10 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.22em] text-white/86"
                      >
                        {pill}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="hv-hero-search-dock absolute left-1/2 -bottom-2 z-30 w-full -translate-x-1/2 px-4">
            <HeroSearchBar />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative bg-transparent">
      <div className="hv-hero-shell relative pb-[112px] md:pb-[120px]">
        <div
          className={`relative overflow-hidden ${isStackedLayout ? "h-auto" : "h-[360px] sm:h-[400px] lg:h-[432px]"}`}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {showAll ? (
            isCarouselLayout ? (
              <div className="flex h-full gap-4">
                {slides.map((banner, index) => renderSlide(banner, index, "h-full min-w-0 flex-1"))}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {slides.map((banner, index) => (
                  <div
                    key={banner.id || `${banner.banner_type}-${index}`}
                    className="relative h-[220px] sm:h-[260px] md:h-[300px]"
                  >
                    {renderSlide(banner, index, "h-full w-full")}
                  </div>
                ))}
              </div>
            )
          ) : (
            <div
              className="flex h-full transition-transform duration-700 ease-out"
              style={{ transform: `translateX(-${activeIndex * 100}%)` }}
            >
              {slides.map((banner, index) => (
                <div key={banner.id || `${banner.banner_type}-${index}`} className="h-full min-w-full flex-shrink-0">
                  {banner.banner_type === "split3"
                    ? renderSplit3(banner, index === activeIndex)
                    : banner.banner_type === "split2"
                    ? renderSplit2(banner, index === activeIndex)
                    : renderFullBanner(banner, index === activeIndex)}
                </div>
              ))}
            </div>
          )}

          {canSlide ? (
            <>
              <button
                type="button"
                aria-label="Previous banner"
                onClick={goPrev}
                className="absolute left-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/12 text-white backdrop-blur transition hover:bg-white/20"
              >
                &lt;
              </button>
              <button
                type="button"
                aria-label="Next banner"
                onClick={goNext}
                className="absolute right-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/12 text-white backdrop-blur transition hover:bg-white/20"
              >
                &gt;
              </button>
            </>
          ) : null}
        </div>

        <div className="hv-hero-search-dock absolute left-1/2 -bottom-2 z-30 w-full -translate-x-1/2 px-4">
          <HeroSearchBar />
        </div>
      </div>
    </section>
  );
}
